

import React, { useState, useEffect, useRef } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Menu,
  MenuItem,
  Avatar,
  Tooltip,
  Divider,
  Box,
  ListItemButton,
  useTheme,
  alpha
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ListAltIcon from "@mui/icons-material/ListAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SettingsIcon from "@mui/icons-material/Settings";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ReceiptIcon from "@mui/icons-material/Receipt";
import TransferWithinAStationIcon from "@mui/icons-material/TransferWithinAStation";
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing';
import DashboardIcon from "@mui/icons-material/Dashboard";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import HomeWorkIcon from "@mui/icons-material/HomeWork";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import BookIcon from "@mui/icons-material/Book";
import { useNavigate, useLocation } from "react-router-dom";
import { styled } from "@mui/system";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { auth } from "./firebase.js";
import logo from "../src/components/Dashboard/QRCode/OV-inventory.jpg";

const Title = styled(Typography)(({ theme }) => ({
  textDecoration: "none",
  color: "inherit",
  flexGrow: 1,
  cursor: "pointer",
  fontFamily: "Roboto, sans-serif",
  fontWeight: 600,
  display: "flex",
  alignItems: "center",
  gap: "10px",
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(2),
  justifyContent: "center",
}));

const LogoText = styled(Typography)({
  fontFamily: "Roboto, sans-serif",
  fontWeight: 700,
  fontSize: "1.2rem",
  color: "#3f51b5",
  marginLeft: "10px",
});

function NavBar({ mode, setMode, isLoggedIn }) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [userData, setUserData] = useState(null);
    const navigate = useNavigate();
  const location = useLocation();
  const hasNavigatedRef = useRef(false);
  const theme = useTheme();

  const isJWTValid = () => {
    const token = sessionStorage.getItem("jwtToken");
    return !!token;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      const tokenValid = isJWTValid();
      if (user && tokenValid) {
        setUserData({
          email: user.email,
          displayName: sessionStorage.getItem("userName") || user.displayName,
        });
        hasNavigatedRef.current = false;
      } else {
        setUserData(null);
        // only redirect if on a truly protected route
        const publicPaths = ["/", "/login", "/new-user-sign-in"];
        if (
          !hasNavigatedRef.current &&
          !publicPaths.includes(location.pathname)
        ) {
          hasNavigatedRef.current = true;
          navigate("/");
        }
      }
    });
    return () => unsubscribe();
  }, [navigate, location.pathname]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    signOut(auth)
      .then(() => {
        setUserData(null);
        sessionStorage.removeItem("jwtToken");
        navigate("/");
        handleMenuClose();
      })
      .catch((error) => {
        console.error("Error signing out: ", error);
      });
  };

  const handleTitleClick = () => {
    if (userData) {
      navigate("/dashboard");
    } else {
      navigate("/");
    }
  };

  const navItems = [
    { text: "Dashboard", icon: <DashboardIcon />, link: "/dashboard" },
    { text: "Inventory", icon: <ListAltIcon />, link: "/inventory_list" },
    {
      text: "Locations",
      icon: <LocationOnIcon />,
      link: "/location_viewer",
      disabled: true,
    },
    { text: "Address", icon: <HomeWorkIcon />, link: "/address" },
    {
      text: "Transfer Logs",
      icon: <TransferWithinAStationIcon />,
      link: "/transferLogs",
    },
    { text: "Sticker", icon: <LocalOfferIcon />, link: "/sticker" },
    { text: "Admin", icon: <SettingsIcon />, link: "/admin" },
    { text: "Reports", icon: <ReceiptIcon />, link: "/reports" },
    {
      text: "Purchase Order",
      icon: <ShoppingCartIcon />,
      link: "/purchase_order",
    },
    { text: "Warehouse", icon: <WarehouseIcon />, link: "/warehouse" },
    { text: "Catalogue", icon: <BookIcon />, link: "/catalogue" },
    { 
      text: "Production", 
      icon: <PrecisionManufacturingIcon />, 
      link: "/production_dashboard",
      hasSubmenu: true
    },
  ];

  return (
    <div>
      <AppBar
        position="fixed"
        sx={{
          backgroundColor: mode === 'light' ? '#1976d2' : '#0d47a1',
          color: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          backdropFilter: 'blur(10px)',
          background: mode === 'light' 
            ? 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)'
            : 'linear-gradient(135deg, #0d47a1 0%, #0a3d8f 100%)',
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            px: { xs: 2, sm: 3 },
            minHeight: { xs: 56, sm: 64 },
          }}
        >
          {userData && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer(true)}
              sx={{
                mr: 2,
                padding: 1.5,
                transition: 'all 0.2s ease',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.12)',
                  transform: 'scale(1.05)',
                },
              }}
            >
              <MenuIcon sx={{ fontSize: 26 }} />
            </IconButton>
          )}
          <Title variant="h6" onClick={handleTitleClick} sx={{
            color: '#fff',
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
            gap: { xs: "8px", sm: "12px" },
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.02)',
            }
          }}>
            <img
              src={logo}
              alt="Logo"
              style={{ width: 36, height: 36, borderRadius: 8, background: 'transparent', filter: 'none' }}
            />
            Assetrack
          </Title>
          <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
            {userData && (
              <>
                <Typography
                  variant="body1"
                  sx={{
                    color: '#fff',
                    fontSize: { xs: "0.95rem", sm: "1.05rem" },
                    display: { xs: "none", sm: "block" },
                  }}
                >
                  {userData.displayName || userData.email}
                </Typography>
                <Tooltip title="Open settings">
                  <IconButton onClick={handleMenuOpen} sx={{ p: 0 }}>
                    <Avatar sx={{ bgcolor: theme.palette.primary.main, color: theme.palette.getContrastText(theme.palette.primary.main) }}>
                      {userData.displayName
                        ? userData.displayName.charAt(0)
                        : "U"}
                    </Avatar>
                  </IconButton>
                </Tooltip>
                <Menu
                  sx={{ mt: "45px" }}
                  id="menu-appbar"
                  anchorEl={anchorEl}
                  anchorOrigin={{ vertical: "top", horizontal: "right" }}
                  keepMounted
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  PaperProps={{
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  <MenuItem disabled>
                    <Typography textAlign="center">
                      User: {userData.displayName || userData.email}
                    </Typography>
                  </MenuItem>
                  <MenuItem onClick={handleLogout}>Logout</MenuItem>
                </Menu>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>
      <div style={{ marginTop: 64 }}></div>
      {userData && (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={toggleDrawer(false)}
          PaperProps={{
            sx: {
              width: 300,
              bgcolor: theme.palette.background.paper,
              borderRight: '1px solid rgba(0, 0, 0, 0.06)',
              boxShadow: '0 0 20px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          <DrawerHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', py: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    bgcolor: 'primary.50',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  <img
                    src={logo}
                    alt="Logo"
                    style={{ width: 28, height: 28, borderRadius: 4 }}
                  />
                </Box>
                <LogoText sx={{ color: 'primary.main', fontSize: '1.4rem', fontWeight: 600 }}>Assetrack</LogoText>
              </Box>
              <IconButton
                onClick={toggleDrawer(false)}
                sx={{
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
          </DrawerHeader>
          <Divider sx={{ mx: 2 }} />
          <Box
            role="presentation"
            onClick={toggleDrawer(false)}
            onKeyDown={toggleDrawer(false)}
            sx={{ width: "100%" }}
          >
            <List sx={{ px: 1, py: 1 }}>
              {navItems.map((item) => {
                if (item.hasSubmenu) {
                  return (
                    <React.Fragment key={item.text}>
                      <ListItem 
                        button 
                        onClick={() => navigate('/production_dashboard')}
                        sx={{
                          borderRadius: 2,
                          mx: 1,
                          mb: 0.5,
                          '&:hover': {
                            bgcolor: 'primary.50',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ color: 'primary.main', minWidth: 40 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={item.text}
                          primaryTypographyProps={{
                            fontSize: '0.95rem',
                            fontWeight: 500,
                          }}
                        />
                      </ListItem>
                    </React.Fragment>
                  );
                }
                
                return (
                  <ListItem
                    key={item.text}
                    button={!item.disabled}
                    disabled={item.disabled}
                    onClick={() => !item.disabled && navigate(item.link)}
                    sx={{
                      borderRadius: 2,
                      mx: 1,
                      mb: 0.5,
                      ...(item.disabled && { opacity: 0.4 }),
                      '&:hover': {
                        bgcolor: 'primary.50',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ 
                      color: item.disabled ? 'text.secondary' : 'primary.main', 
                      minWidth: 40 
                    }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={item.text}
                      primaryTypographyProps={{
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        color: item.disabled ? 'text.secondary' : 'inherit',
                      }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </Drawer>
      )}
    </div>
  );
}

export default NavBar;
